import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useEmblaCarousel } from 'embla-carousel/react';

import { hasWindow } from '../utils';

import Block from './Block';
import CarouselDots from './CarouselDots';

export const autoPlay = (embla, interval) => {
  const state = { timer: 0 };

  const stop = () => {
    if (hasWindow()) {
      window.clearTimeout(state.timer);
      state.timer = 0;
    }
  };

  const play = () => {
    stop();

    if (hasWindow()) {
      requestAnimationFrame(() => {
        state.timer = window.setTimeout(next, interval); // eslint-disable-line no-use-before-define
      });
    }
  };

  const next = () => {
    if (embla.canScrollNext()) {
      embla.scrollNext();
    } else {
      embla.scrollTo(0);
    }

    play();
  };

  return { play, stop };
};

const Carousel = ({
  children,
  autoplay,
  slideInterval,
  dotColor,
  dotsInsideCarousel,
}) => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false });
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    let autoPlayer;

    if (embla) {
      embla.on('select', () => setSlide(embla.selectedScrollSnap()));

      if (autoplay) {
        autoPlayer = autoPlay(embla, slideInterval * 1000);
        autoPlayer.play();
      }
    }

    return () => {
      if (embla) {
        if (autoPlayer) {
          autoPlayer.stop();
        }

        embla.destroy();
      }
    };
  }, [embla]);

  return (
    <Block ref={emblaRef} style={{ overflow: 'hidden' }}>
      <Block display="flex" position="relative">
        {children.map(child => child)}
      </Block>

      {embla && embla.scrollSnapList().length > 1 ? (
        <CarouselDots insideCarousel={dotsInsideCarousel}>
          {embla.scrollSnapList().map((_, index) => (
            <CarouselDots.Dot
              key={index}
              index={index}
              length={embla && embla.scrollSnapList().length}
              active={index === slide}
              color={dotColor}
              onClick={() => embla.scrollTo(index)}
            />
          ))}
        </CarouselDots>
      ) : null}
    </Block>
  );
};

Carousel.defaultProps = {
  dotColor: 'white',
  dotsInsideCarousel: false,
};

Carousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node.isRequired).isRequired,
  autoplay: PropTypes.bool.isRequired,
  slideInterval: PropTypes.number.isRequired,
  dotColor: PropTypes.string,
  dotsInsideCarousel: PropTypes.bool,
};

export default Carousel;
